import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFetcher } from 'react-router';
import {
    CREATE_CONVERSATION_API,
    type action,
} from '~/routes/remix-api.chat.create-conversation';
import { useUserLoggedIn } from '~/modules/user';
import type { Conversation, Message, TextOrFileMessage } from '../types';
import { createOptimisticMessage } from '../utils';
import { useConversationCache } from './use-conversation-cache';

/**
 * Hook to create a new conversation with the given participants.
 */
export function useCreateConversation({
    memberIDs,
    professionalIDs,
    conversation,
}: {
    /**
     * The member IDs of the other participants.
     */
    memberIDs: number[];
    /**
     * The professional IDs of the other participants.
     */
    professionalIDs: number[];
    /**
     * The currently loaded preview conversation, if any. Only used to enrich
     * the optimistic message with the real self-participant.
     */
    conversation?: Conversation | null;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const { cacheConversation } = useConversationCache();
    const createFetcher = useFetcher<typeof action>();
    const [optimisticMessage, setOptimisticMessage] = useState<Message | null>(
        null,
    );
    const hasParticipants = memberIDs.length > 0 || professionalIDs.length > 0;
    const isSubmitting = createFetcher.state === 'submitting';
    const createdConversation = useMemo(() => {
        return createFetcher.data?.conversation ?? null;
    }, [createFetcher.data]);

    const createConversation = useCallback(
        (
            message?: string,
            attachments?: File[],
            replyTo?: TextOrFileMessage,
        ) => {
            if (!hasParticipants || isSubmitting || !isLoggedIn) {
                return;
            }
            const formData = new FormData();
            for (const participantID of memberIDs) {
                formData.append('memberIDs', String(participantID));
            }
            for (const participantID of professionalIDs) {
                formData.append('professionalIDs', String(participantID));
            }
            if (message) {
                formData.append('message', message);
            }
            if (attachments?.length) {
                attachments.forEach((attachment) => {
                    formData.append('attachments', attachment);
                });
            }
            if (replyTo) {
                formData.append('replyToID', String(replyTo.ID));
            }
            createFetcher.submit(formData, {
                action: CREATE_CONVERSATION_API,
                method: 'POST',
                encType: attachments?.length
                    ? 'multipart/form-data'
                    : undefined,
            });
            setOptimisticMessage(
                createOptimisticMessage({
                    conversationID: Date.now(),
                    professionalID: null,
                    message,
                    attachments,
                    replyTo,
                    conversation,
                    user,
                }),
            );
        },
        [
            hasParticipants,
            memberIDs,
            professionalIDs,
            isSubmitting,
            isLoggedIn,
            user,
            conversation,
            createFetcher,
        ],
    );

    // Cache the created conversation.
    useEffect(() => {
        if (createdConversation) {
            cacheConversation(createdConversation, null);
        }
    }, [createdConversation, cacheConversation]);

    return {
        /**
         * The conversation that was created by the `createConversation` function.
         * This is `null` if not created yet.
         */
        createdConversation,
        /**
         * The message object that was created by the `createConversation` function.
         * This can be used for optimistic UI.
         */
        optimisticMessage,
        /**
         * The conversation is currently being created.
         */
        isSubmitting,
        /**
         * Create a new conversation.
         */
        createConversation,
    };
}
